import { useState, useRef } from 'react';
import { RightViewType, AIGenerationHookParams } from './ai/types';
import { useResumeAnalysis } from './useResumeAnalysis';
import { useCoverLetter } from './useCoverLetter';
import { useTailoredResume } from './useTailoredResume';
import { useInterviewPrep } from './useInterviewPrep';
import { useCommunications } from './useCommunications';

export type { RightViewType } from './ai/types';

export function useAIGeneration(props: any) {
  const { workspaceId, selectedId, jobText, targetDetails, setStatus, setStatusMessage, recordUsage } = props;
  
  const [rightView, setRightView] = useState<RightViewType>('empty');
  const [activeAnalysisTab, setActiveAnalysisTab] = useState('overview');
  const [isGenerating, setIsGenerating] = useState<Record<string, boolean>>({});
  const abortControllersRef = useRef<Record<string, AbortController>>({});

  const scrollToResults = () => {
    setTimeout(() => {
      document.getElementById('results-area')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const getSignal = (key: string) => {
    if (abortControllersRef.current[key]) {
      abortControllersRef.current[key].abort();
    }
    abortControllersRef.current[key] = new AbortController();
    return abortControllersRef.current[key].signal;
  };

  const cancelGeneration = () => {
    Object.values(abortControllersRef.current).forEach(ac => ac.abort());
    setIsGenerating({});
    setStatus('idle');
    setStatusMessage('Generation canceled by user.');
  };

  const hookParams: AIGenerationHookParams = {
    workspaceId,
    selectedId,
    jobText,
    targetDetails,
    setStatus,
    setStatusMessage,
    recordUsage,
    setRightView,
    getSignal,
    setIsGenerating,
    scrollToResults,
  };

  const { reviewResult, setReviewResult, handleAnalyze } = useResumeAnalysis(hookParams);
  const { coverLetterResult, setCoverLetterResult, handleGenerateCoverLetter } = useCoverLetter(hookParams);
  const { tailoredResumeResult, setTailoredResumeResult, handleGenerateTailoredResume } = useTailoredResume(hookParams);
  const { interviewPrepResult, setInterviewPrepResult, handleGenerateInterviewPrep } = useInterviewPrep(hookParams);
  const { communicationsResult, setCommunicationsResult, handleGenerateCommunications } = useCommunications(hookParams);

  const handleGeneratePackage = async () => {
    if (!selectedId || jobText.trim().length < 50) {
      setStatus('error');
      setStatusMessage('Please enter a longer, meaningful job description.');
      return;
    }

    setStatus('working');
    setStatusMessage('Generating application package (This may take a minute or two). Please wait...');
    
    // Determine if it's the first time and we lack data
    if ((!reviewResult || (!reviewResult.review && !coverLetterResult && !tailoredResumeResult)) && (rightView === 'history' || rightView === 'empty')) {
      setRightView('analysis');
      scrollToResults();
    }

    // Custom toast event for User Notification
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent('resumepro:show-toast', { 
        detail: 'Generating package sequentially for safety: Analysis → Tailored Resume → Cover Letter → Interview Prep → Outreach.'
      }));
    }
    
    try {
      getSignal('package'); // clear previous
      
      const analysisResult = await handleAnalyze(true);
      const recommendations = analysisResult?.review?.sectionRecommendations || [];
      
      setStatusMessage('Analysis complete. Generating tailored resume...');
      await handleGenerateTailoredResume(recommendations, true).catch(e => {});
      
      setStatusMessage('Resume complete. Generating cover letter...');
      await handleGenerateCoverLetter(true).catch(e => {});
      
      setStatusMessage('Cover letter complete. Generating interview prep...');
      await handleGenerateInterviewPrep(true).catch(e => {});
      
      setStatusMessage('Interview prep complete. Generating outreach messages...');
      await handleGenerateCommunications(true).catch(e => {});
      
      setStatus('idle');
      setStatusMessage('Application package ready! Click "Save Output" to keep it in history.');
      
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent('resumepro:show-toast', { 
          detail: '✨ Application package fully generated! Click "Save Output" to keep it in your history.'
        }));
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      setStatus('error');
      setStatusMessage(err.message || 'Package generation failed');
    }
  };

  const resetResults = () => {
    setReviewResult(null);
    setCoverLetterResult(null);
    setInterviewPrepResult(null);
    setCommunicationsResult(null);
    setTailoredResumeResult(null);
    setRightView('empty');
    setIsGenerating({});
  };

  return {
    reviewResult, setReviewResult,
    coverLetterResult, setCoverLetterResult,
    interviewPrepResult, setInterviewPrepResult,
    communicationsResult, setCommunicationsResult,
    tailoredResumeResult, setTailoredResumeResult,
    rightView, setRightView,
    activeAnalysisTab, setActiveAnalysisTab,
    handleAnalyze, handleGenerateCoverLetter, handleGenerateTailoredResume, handleGenerateInterviewPrep, handleGenerateCommunications, handleGeneratePackage,
    resetResults,
    isGenerating,
    cancelGeneration
  };
}
